import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import "./VendorMasterForm.css";

const BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in"
).replace(/\/+$/, "");

const EMPTY_FORM = {
  fullName: "",
  mobile: "",
  aadhaarNumber: "",
  panNumber: "",
  gstCertificate: "",
  currentResi: "",
  address: "",
  email: "",
  vendorType: "",
};

const blankStage = () => ({ stageName: "", percentage: "" });
const totalPercent = (rows) =>
  rows.reduce(
    (sum, row) => sum + Math.round(Number(row.percentage || 0) * 100),
    0,
  ) / 100;

const VENDOR_FIELDS = [
  { name: "fullName", label: "Full Name" },
  { name: "mobile", label: "Mobile" },
  { name: "aadhaarNumber", label: "Aadhaar Number" },
  { name: "panNumber", label: "PAN Number" },
  { name: "gstCertificate", label: "GST Certificate" },
  { name: "email", label: "Email" },
  { name: "currentResi", label: "Current Residence" },
  { name: "address", label: "Permanent Address" },
];

const VENDOR_TYPES = [
  "Carpenter",
  "General contractor",
  "Electrician",
  "Plumber",
  "False Celling",
  "Painter",
  "Sofa Maker",
  "Curtain maker",
  "Loose furniture maker",
  "Wall texture maker",
  "Wallpaper maker",
  "Material supply (all)",
  "Civil work contractor",
  "Kitchen countertop fitter",
  "Modular fitter",
  "Fabricator",
  "Deep cleaning",
  "Debris cleaning",
];

const getAuthToken = () => {
  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("userToken") ||
    localStorage.getItem("adminToken") ||
    storedUser?.token ||
    storedUser?.accessToken ||
    ""
  );
};

const getAuthConfig = () => {
  const token = getAuthToken();

  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};

const getVendorImageUrl = (filePath) => {
  if (!filePath) return "";

  const path = String(filePath).trim().replace(/\\/g, "/");

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (path.startsWith("/api/") || path.startsWith("/uploads/")) {
    return `${BASE_URL}${path}`;
  }

  return `${BASE_URL}/uploads/${path.replace(/^\/+/, "")}`;
};

const VendorPhoto = ({ file, label }) => {
  const [imageError, setImageError] = useState(false);

  const url = getVendorImageUrl(file);

  if (!url) return <span className="vm-empty">—</span>;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="vm-photo-link"
      title={`Open ${label}`}
    >
      {!imageError ? (
        <img
          src={url}
          alt={label}
          className="vm-photo"
          onError={() => setImageError(true)}
        />
      ) : (
        <span className="vm-file-fallback">View File</span>
      )}
    </a>
  );
};

const VendorMasterForm = () => {
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const [photo1, setPhoto1] = useState(null);
  const [photo2, setPhoto2] = useState(null);

  const [vendors, setVendors] = useState([]);
  const [editId, setEditId] = useState(null);
  const [paymentStages, setPaymentStages] = useState([blankStage()]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loadError, setLoadError] = useState("");

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const response = await axios.get(
        `${BASE_URL}/api/vendors`,
        getAuthConfig(),
      );

      const data = response.data;

      // Only replace the table when the API returns a valid list.
      if (!Array.isArray(data)) {
        throw new Error("Vendor API returned an unexpected response.");
      }

      setVendors(data);
    } catch (error) {
      console.error("Error fetching vendors:", error);

      setLoadError(`Unable to refresh vendor list: ${getErrorMessage(error)}`);

      // IMPORTANT:
      // Do not call setVendors([]) here.
      // Keep previously loaded records visible.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleInput = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setPhoto1(null);
    setPhoto2(null);
    setEditId(null);
    setPaymentStages([blankStage()]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) return;
    const sum = totalPercent(paymentStages);
    if (
      !paymentStages.length ||
      paymentStages.some(
        (row) =>
          !row.stageName.trim() ||
          row.percentage === "" ||
          !Number.isFinite(Number(row.percentage)) ||
          Number(row.percentage) <= 0 ||
          Number(row.percentage) > 100,
      ) ||
      Math.abs(sum - 100) > 0.001
    ) {
      alert(
        `Please enter valid payment stages. Total must be exactly 100% (current: ${sum.toFixed(2)}%).`,
      );
      return;
    }
    setSaving(true);

    try {
      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value ?? "");
      });

      formData.append(
        "paymentStages",
        JSON.stringify(
          paymentStages.map((row) => ({
            stageName: row.stageName.trim(),
            percentage: Number(row.percentage),
          })),
        ),
      );
      if (photo1) {
        formData.append("photo1", photo1);
      }

      if (photo2) {
        formData.append("photo2", photo2);
      }

      if (editId) {
        await axios.put(
          `${BASE_URL}/api/vendors/${editId}`,
          formData,
          getAuthConfig(),
        );

        alert("Vendor updated successfully!");
      } else {
        await axios.post(
          `${BASE_URL}/api/vendors/create`,
          formData,
          getAuthConfig(),
        );

        alert("Vendor saved successfully!");
      }

      resetForm();

      // Refresh without clearing existing rows if API fails.
      await fetchVendors();
    } catch (error) {
      console.error("Error saving vendor:", error);

      alert(`Failed to save/update vendor: ${getErrorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (vendor) => {
    setForm({
      fullName: vendor.fullName || "",
      mobile: vendor.mobile || "",
      aadhaarNumber: vendor.aadhaarNumber || "",
      panNumber: vendor.panNumber || "",
      gstCertificate: vendor.gstCertificate || "",
      currentResi: vendor.currentResi || "",
      address: vendor.address || "",
      email: vendor.email || "",
      vendorType: vendor.vendorType || "",
    });

    setPhoto1(null);
    setPhoto2(null);
    setEditId(vendor._id);
    setPaymentStages(
      Array.isArray(vendor.paymentStages) && vendor.paymentStages.length
        ? vendor.paymentStages.map((stage) => ({
            stageName: stage.stageName || "",
            percentage: String(stage.percentage ?? ""),
          }))
        : [blankStage()],
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = async (id) => {
    if (deletingId) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this vendor?",
    );

    if (!confirmed) return;

    setDeletingId(id);

    try {
      await axios.delete(`${BASE_URL}/api/vendors/${id}`, getAuthConfig());

      // Remove only after server confirms deletion.
      setVendors((previous) => previous.filter((vendor) => vendor._id !== id));

      if (editId === id) {
        resetForm();
      }

      alert("Vendor deleted successfully!");
    } catch (error) {
      console.error("Error deleting vendor:", error);

      alert(`Failed to delete vendor: ${getErrorMessage(error)}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="vendor-master">
      <div className="vm-page-heading">
        <h3>Vendor Master Form</h3>

        <span className="vm-count">Total Vendors: {vendors.length}</span>
      </div>

      <form
        onSubmit={handleSubmit}
        encType="multipart/form-data"
        className="vm-form-card"
      >
        <div className="vendor-form-grid">
          {VENDOR_FIELDS.map((field) => (
            <div className="vendor-field" key={field.name}>
              <label htmlFor={`vendor-${field.name}`}>{field.label}</label>

              <input
                id={`vendor-${field.name}`}
                type="text"
                name={field.name}
                className="form-control"
                value={form[field.name]}
                onChange={handleInput}
              />
            </div>
          ))}

          <div className="vendor-field">
            <label htmlFor="vendor-type">Vendor Type</label>

            <select
              id="vendor-type"
              name="vendorType"
              className="form-control"
              value={form.vendorType}
              onChange={handleInput}
            >
              <option value="">Select Vendor Type</option>

              {VENDOR_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="vendor-field">
            <label htmlFor="vendor-photo1">
              Upload Photo 1 (Aadhaar / PAN)
            </label>

            <input
              id="vendor-photo1"
              type="file"
              className="form-control"
              accept="image/*,.pdf"
              onChange={(event) => setPhoto1(event.target.files?.[0] || null)}
            />
          </div>

          <div className="vendor-field">
            <label htmlFor="vendor-photo2">Upload Photo 2 (GST / Other)</label>

            <input
              id="vendor-photo2"
              type="file"
              className="form-control"
              accept="image/*,.pdf"
              onChange={(event) => setPhoto2(event.target.files?.[0] || null)}
            />
          </div>
        </div>

        <section
          className="vm-vendor-stages"
          aria-label="Vendor payment stages"
        >
          <div className="vm-vendor-stage-heading">
            <h4>Payment Stages — This Vendor</h4>
            <button
              type="button"
              className="btn vm-refresh-btn"
              onClick={() => setPaymentStages((old) => [...old, blankStage()])}
            >
              + Add Stage
            </button>
          </div>
          <div className="table-responsive">
            <table className="vm-vendor-stage-table">
              <thead>
                <tr>
                  <th>Sr.</th>
                  <th>Stage Name</th>
                  <th>Payment %</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paymentStages.map((stage, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <input
                        className="form-control"
                        type="text"
                        maxLength={100}
                        required
                        value={stage.stageName}
                        placeholder="e.g. Advance"
                        onChange={(event) =>
                          setPaymentStages((old) =>
                            old.map((row, i) =>
                              i === index
                                ? { ...row, stageName: event.target.value }
                                : row,
                            ),
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        required
                        value={stage.percentage}
                        placeholder="0.00"
                        onChange={(event) =>
                          setPaymentStages((old) =>
                            old.map((row, i) =>
                              i === index
                                ? { ...row, percentage: event.target.value }
                                : row,
                            ),
                          )
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={paymentStages.length === 1}
                        onClick={() =>
                          setPaymentStages((old) =>
                            old.filter((_, i) => i !== index),
                          )
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Total (must equal 100%)</td>
                  <td
                    className={
                      Math.abs(totalPercent(paymentStages) - 100) < 0.001
                        ? "vm-stage-valid"
                        : "vm-stage-invalid"
                    }
                  >
                    {totalPercent(paymentStages).toFixed(2)}%
                  </td>
                  <td>
                    {Math.abs(totalPercent(paymentStages) - 100) < 0.001
                      ? "Ready"
                      : "Incomplete"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <div className="vm-form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving..." : editId ? "Update Vendor" : "Save Vendor"}
          </button>

          {editId && (
            <button
              type="button"
              className="btn vm-cancel-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="vm-list-heading">
        <h4>Saved Vendor Details</h4>

        <button
          type="button"
          className="btn vm-refresh-btn"
          onClick={fetchVendors}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {loadError && (
        <div className="vm-error" role="alert">
          {loadError}
          {vendors.length > 0 && (
            <span> Previously loaded records are still displayed.</span>
          )}
        </div>
      )}

      <div className="card vm-table-card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered table-hover text-center align-middle">
              <thead>
                <tr>
                  <th>Sr No</th>
                  <th>Vendor Code</th>
                  <th>Full Name</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Vendor Type</th>
                  <th>Aadhaar No</th>
                  <th>PAN No</th>
                  <th>GST No</th>
                  <th>Address</th>
                  <th>Photo 1</th>
                  <th>Photo 2</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {vendors.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="vm-empty-row">
                      {loading
                        ? "Loading vendors..."
                        : loadError
                          ? "Unable to load vendors. Click Refresh to retry."
                          : "No vendor data available"}
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor, index) => (
                    <tr key={vendor._id}>
                      <td>{index + 1}</td>

                      <td>{vendor.vendorCode || "—"}</td>

                      <td>{vendor.fullName || "—"}</td>

                      <td>{vendor.mobile || "—"}</td>

                      <td>{vendor.email || "—"}</td>

                      <td>{vendor.vendorType || "—"}</td>

                      <td>{vendor.aadhaarNumber || "—"}</td>

                      <td>{vendor.panNumber || "—"}</td>

                      <td>{vendor.gstCertificate || "—"}</td>

                      <td className="vm-address" title={vendor.address || ""}>
                        {vendor.address || "—"}
                      </td>

                      <td>
                        <VendorPhoto
                          file={vendor.photo1}
                          label="Vendor Photo 1"
                        />
                      </td>

                      <td>
                        <VendorPhoto
                          file={vendor.photo2}
                          label="Vendor Photo 2"
                        />
                      </td>

                      <td>
                        <div className="vm-row-actions">
                          <button
                            type="button"
                            className="btn btn-warning"
                            onClick={() => handleEdit(vendor)}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => handleDelete(vendor._id)}
                            disabled={deletingId === vendor._id}
                          >
                            {deletingId === vendor._id
                              ? "Deleting..."
                              : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorMasterForm;
